/**
 * The catalogue of everything the app is allowed to say when the voice is on.
 *
 * ── Voice rules (every line, no exceptions) ─────────────────────────────────
 * 1. Wednesday base: declarative sentences, deadpan, morbid imagery welcome.
 *    No exclamation marks, no emoji, no enthusiasm. The joke buries the
 *    situation and the numbers — never the person's worth.
 * 2. Camp is delivery, not announcement. The pet name is „branguti" (masculine
 *    vocative), at most one line in ten; never „širdele".
 * 3. English sprinkles: at most one per line, lowercase, unquoted (slay, RIP,
 *    iconic), and only where a Vilnius twenty-something would drop it.
 * 4. Numbers roast AND help: every over-budget line carries at least one
 *    useful figure (what is over, tomorrow's budget, what remains). Decimal
 *    comma, U+2212 for minus — use the fmt helpers, never inline arithmetic.
 * 5. Names only through vocativeLt (the `voc` helper). Mostly in greetings.
 * 6. First person avoids gendered forms where Lithuanian allows it; where a
 *    form is unavoidable, the voice is feminine — it knows who it takes after.
 * 7. Length is enforced by tests against the budgets in `sass.ts`: toast
 *    templates ≤ TOAST_MAX_CHARS (120) at maximal context, screen lines ≤ 160.
 *    Ninety was the old toast budget and the reason this catalogue drifted
 *    into engraved epigrams — at that width the only sentence that fits is a
 *    squeezed one. Three clamped lines is the stage now.
 * 8. Saulius cameos live in CAMEO, ride along only on accounts that are not
 *    his, stay sparing, and are affectionate-menacing — never surveillance.
 *
 * Pools are keyed `event.bucket` with optional `.foodClass` or `.phase`
 * overrides; the engine cascades from the most specific key down to
 * `event.base`. POOL_META declares how often each pool is expected to fire per
 * 14 days — the lint test demands strictly more lines than that, which is what
 * makes the no-repeat window an invariant instead of an aspiration.
 *
 * Three cast pools ride along the base catalogue without counting toward
 * POOL_META (see `engine.ts` for the merge): CAMEO (Saulius, shown only where
 * `cameoOk`), KATE (the household cat, shown on every account), and EIMANTAS
 * (material about the listener himself, shown only where `cameoOk` — same
 * gate as the Saulius cameo, since it only makes sense addressed to him).
 *
 * Three catalogues replace the pool outright rather than riding along, and so
 * are likewise exempt from POOL_META: LOYAL (a struggling week), NEGLECTED (a
 * silent diary, keyed `event.tier` — see `mood.ts`), and HERLIFE (a day she
 * has something of her own to report — see `herlife.ts`).
 */

import type { MoodKey, SassContext } from '@gramas/shared'
import { AMBIENT, AMBIENT_META } from './ambient.js'
import { BUDGET, BUDGET_META } from './budget.js'
import { CAMEO_POOLS } from './cameo.js'
import { DELETES, DELETES_META } from './deletes.js'
import { EGGS } from './eggs.js'
import { EIMANTAS_POOLS } from './eimantas.js'
import { EMPTY, EMPTY_META } from './empty.js'
import { ERRORS, ERRORS_META } from './errors.js'
import { EXCLUSIVES, type Exclusive } from './exclusive.js'
import { FESTIVE_POOLS } from './festive.js'
import { FOODS, FOODS_META } from './foods.js'
import { GLOOMY_POOLS } from './gloomy.js'
import { GREETINGS, GREETINGS_META } from './greetings.js'
import { HERLIFE_POOLS } from './herlife.js'
import {
  AFFECTION_COOLED as AFFECTION_COOLED_POOLS,
  AFFECTION_WARMED as AFFECTION_WARMED_POOLS,
  INTERACTION,
  INTERACTION_META,
} from './interaction.js'
import { KATE_POOLS } from './kate.js'
import { LOADING, LOADING_META } from './loading.js'
import { LOGGING, LOGGING_META } from './logging.js'
import { LOGIN, LOGIN_META } from './login.js'
import { LOYAL_POOLS } from './loyal.js'
import { NEGLECTED_POOLS } from './neglected.js'
import { PRESENCE, PRESENCE_META } from './presence.js'
import { PROUD_POOLS } from './proud.js'
import { SCALE, SCALE_META } from './scale.js'
import { SCREENS, SCREENS_META } from './screens.js'

export type Line = (ctx: SassContext) => string
export type Pool = ReadonlyArray<Line>

export const CATALOG: Readonly<Record<string, Pool>> = {
  ...GREETINGS,
  ...LOGIN,
  ...LOGGING,
  ...FOODS,
  ...DELETES,
  ...BUDGET,
  ...SCREENS,
  ...ERRORS,
  ...EMPTY,
  ...LOADING,
  ...PRESENCE,
  ...INTERACTION,
  ...AMBIENT,
  ...SCALE,
}

export const CAMEO: Readonly<Record<string, Pool>> = CAMEO_POOLS
export const KATE: Readonly<Record<string, Pool>> = KATE_POOLS
export const EIMANTAS: Readonly<Record<string, Pool>> = EIMANTAS_POOLS
export const LOYAL: Readonly<Record<string, Pool>> = LOYAL_POOLS
export const NEGLECTED: Readonly<Record<string, Pool>> = NEGLECTED_POOLS

/**
 * The moods that replace a pool rather than riding along with it, in one
 * table so the engine looks them up rather than branching on each.
 *
 * `loyal` used to be the only one of these wired in, and even it was mostly
 * unreachable: it fires only where the call site passes a mood, and exactly
 * one call site did. `proud`, `gloomy` and `festive` were computed on every
 * log commit and had nowhere to go at all. `neglected` is deliberately absent
 * here — it keys on how long the silence has run rather than on the bucket,
 * so the engine handles it separately.
 *
 * Exempt from POOL_META like every other replacing catalogue: a mood pool is
 * only ever drawn on the days that mood is in force, so the fourteen-day
 * window means something different for it than for the base catalogue.
 */
export const MOOD_OVERLAYS: Readonly<Partial<Record<MoodKey, Readonly<Record<string, Pool>>>>> = {
  loyal: LOYAL_POOLS,
  proud: PROUD_POOLS,
  gloomy: GLOOMY_POOLS,
  festive: FESTIVE_POOLS,
}
/**
 * What she has to say about her own day, keyed by the ids `herlife.ts`
 * produces. Replaces the greeting outright when the seed says today is one of
 * hers, and so is exempt from POOL_META like the other overlay catalogues.
 */
export const HERLIFE: Readonly<Record<string, Pool>> = HERLIFE_POOLS
/**
 * The food-aware slice of `CATALOG`, re-exported under its own name purely so
 * the review dump can show it as one batch. It is an ordinary part of the base
 * catalogue in every other respect — same cascade, same POOL_META.
 */
export const FOODS_ONLY: Readonly<Record<string, Pool>> = FOODS
export const EXCLUSIVE: Readonly<Record<string, ReadonlyArray<Exclusive>>> = EXCLUSIVES
export const EASTER_EGGS: Readonly<Record<string, ReadonlyArray<Exclusive>>> = EGGS
/**
 * The affection shades from the interaction layer. Cooled replaces a pool the
 * way a mood overlay does (below every real mood — see `resolvePool`); warmed
 * rides along the way the cast does. Both exempt from POOL_META like every
 * other overlay: they only ever speak on the days the shade is in force.
 */
export const AFFECTION_COOLED: Readonly<Record<string, Pool>> = AFFECTION_COOLED_POOLS
export const AFFECTION_WARMED: Readonly<Record<string, Pool>> = AFFECTION_WARMED_POOLS

/** Expected draws per pool per 14 days; pools must hold strictly more lines. */
export const POOL_META: Readonly<Record<string, number>> = {
  ...GREETINGS_META,
  ...LOGIN_META,
  ...LOGGING_META,
  ...FOODS_META,
  ...DELETES_META,
  ...BUDGET_META,
  ...SCREENS_META,
  ...ERRORS_META,
  ...EMPTY_META,
  ...LOADING_META,
  ...PRESENCE_META,
  ...INTERACTION_META,
  ...AMBIENT_META,
  ...SCALE_META,
}
